/**
 * UserChip — header identity chip (ClickPay login UI).
 *
 * Renders ONLY when AuthProvider resolved an authenticated identity
 * (CLICKPAY_AUTH=on + valid session). In 'open' mode (auth off) it renders
 * nothing, so the header looks exactly as it does today.
 *
 * Visual language mirrors OperatorSelector's chip + dropdown (violet pill,
 * rounded-md menu, lucide icons). Note the two are intentionally separate:
 * OperatorSelector is the legacy edit-attribution picker; this chip is the
 * real authenticated identity. When auth is on, the backend overrides the
 * X-User header with the session identity anyway.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Check, ChevronDown, KeyRound, Loader2, LogOut, Settings, ShieldCheck,
  UserCircle2,
} from 'lucide-react'
import { useAuth } from '../lib/auth'
import { authAdminApi } from '../lib/authAdminApi'
import { SETTINGS_PATH } from '../pages/settings/routing'

/**
 * ChangePasswordModal — self-service password change for the logged-in user.
 *
 * Three fields (current / new / confirm-new) with client-side checks (new ===
 * confirm, new length >= 8) before POSTing to /api/auth/change-password. On
 * success it shows a brief confirmation, clears the fields, and auto-closes;
 * server errors (e.g. "current password is incorrect") render inline. Styling
 * mirrors the app's existing modal (ManualLineItemsModal): fixed overlay +
 * bg-black/40 backdrop + centered white card.
 */
function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  // Escape closes the modal (matches the app's other dialogs) unless a
  // request is in flight.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setError(null)
    if (next.length < 8) {
      setError('New password must be at least 8 characters.')
      return
    }
    if (next !== confirm) {
      setError('New password and confirmation do not match.')
      return
    }
    setBusy(true)
    try {
      await authAdminApi.changePassword(current, next)
      setCurrent(''); setNext(''); setConfirm('')
      setDone(true)
      // Brief confirmation, then close. Session is NOT revoked server-side,
      // so the user stays signed in on this device.
      setTimeout(onClose, 1200)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const inputCls = 'w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={() => !busy && onClose()} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Change password"
        className="relative bg-white rounded-md shadow-xl w-[400px] max-w-[95vw] flex flex-col"
      >
        <header className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5">
            <KeyRound size={15} className="text-gray-400" /> Change password
          </h2>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            className="text-gray-500 hover:text-gray-900 px-2 leading-none disabled:opacity-50"
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>

        <form onSubmit={onSubmit} className="p-4 space-y-3">
          {done ? (
            <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              <Check size={16} className="shrink-0 text-emerald-600" />
              Password changed.
            </div>
          ) : (
            <>
              {error && (
                <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {error}
                </div>
              )}
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Current password</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={current}
                  onChange={e => setCurrent(e.target.value)}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">New password <span className="text-gray-400">(min 8 characters)</span></span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={next}
                  onChange={e => setNext(e.target.value)}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Confirm new password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  className={inputCls}
                />
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy || !current || !next || !confirm}
                  className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                  Change password
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  )
}

export default function UserChip() {
  const { mode, user, logout, isAdmin } = useAuth()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [pwModalOpen, setPwModalOpen] = useState(false)

  if (mode !== 'authed' || !user) return null

  const label = user.display_name || user.email || user.name

  // Settings opens on Users for everyone (PR 7); admins see more tabs there.
  const onSettings = () => { setOpen(false); navigate(SETTINGS_PATH) }

  const onChangePassword = () => { setOpen(false); setPwModalOpen(true) }

  const onLogout = async () => {
    if (busy) return
    setBusy(true)
    try {
      await logout()
      // AuthProvider flips to 'login'; this chip unmounts with the shell.
    } finally {
      setBusy(false)
      setOpen(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded border bg-brand-50 text-brand-700 border-brand-100 hover:opacity-90"
        title={`Signed in as ${user.email ?? user.name}`}
      >
        <UserCircle2 size={13} />
        {label}
        {isAdmin && <ShieldCheck size={12} className="text-brand-600" aria-label="Admin" />}
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          {/* Click-away catcher (same pattern as OperatorSelector) */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-40 min-w-[200px] bg-white border border-gray-200 rounded-md shadow-lg py-1">
            <div className="px-3 py-2 border-b border-gray-100">
              <div className="text-sm font-medium text-gray-900 truncate">{label}</div>
              <div className="text-xs text-gray-500 truncate">{user.email ?? user.name}</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {isAdmin && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                    <ShieldCheck size={10} /> admin
                  </span>
                )}
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${user.can_write_ops ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                      title={user.can_write_ops ? 'Can write inventory/ops data' : 'Read-only on inventory/ops data'}>
                  ops {user.can_write_ops ? 'write' : 'read'}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${user.can_write_ads ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                      title={user.can_write_ads ? 'Can write PPC/ads data' : 'Read-only on PPC/ads data'}>
                  ads {user.can_write_ads ? 'write' : 'read'}
                </span>
              </div>
            </div>
            <button onClick={onSettings} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <Settings size={14} className="text-gray-400" />
              Settings
            </button>
            <button onClick={onChangePassword} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              <KeyRound size={14} className="text-gray-400" />
              Change password
            </button>
            <button
              onClick={onLogout}
              disabled={busy}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <LogOut size={14} className="text-gray-400" />
              {busy ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </>
      )}
      {pwModalOpen && <ChangePasswordModal onClose={() => setPwModalOpen(false)} />}
    </div>
  )
}
